import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';

const ProtectedVideoThumbnail = ({ uuid, className }) => {
  const { accessToken, guestToken } = useAuthStore();
  const [videoSrc, setVideoSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const token = accessToken || guestToken;

    if (!uuid || !token) {
      setLoading(false);
      return;
    }

    const fetchVideo = async () => {
      try {
        setLoading(true);
        setError(false);

        const response = await fetch(`/api/media/${uuid}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to load video');
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        setVideoSrc(objectUrl);
      } catch (err) {
        console.error('Failed to load protected video thumbnail:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchVideo();

    return () => {
      if (videoSrc) {
        URL.revokeObjectURL(videoSrc);
      }
    };
  }, [uuid, accessToken, guestToken]);

  // When video loads metadata, seek to first frame
  useEffect(() => {
    if (videoRef.current && videoSrc) {
      const video = videoRef.current;

      const handleLoadedMetadata = () => {
        video.currentTime = 0.1; // Seek to 0.1 seconds to get first frame
      };

      video.addEventListener('loadedmetadata', handleLoadedMetadata);

      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [videoSrc]);

  if (loading) {
    return (
      <div className={`bg-gray-200 dark:bg-gray-700 animate-pulse ${className}`} />
    );
  }

  if (error || !videoSrc) {
    return (
      <div className={`bg-gray-300 dark:bg-gray-600 flex items-center justify-center ${className}`}>
        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <video
        ref={videoRef}
        src={videoSrc}
        preload="metadata"
        className="w-full h-full object-cover"
        muted
      />
      {/* Play icon overlay */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="bg-black bg-opacity-50 rounded-full p-3">
          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default ProtectedVideoThumbnail;
